import { useEffect, useState } from "react";
import COLORS from "@/utils/Colors";

// ── Tipos ─────────────────────────────────────────────────────
interface Opcion {
  label: string;
  value: number;
}

interface ReactivoProps {
  /** Texto de la pregunta */
  pregunta: string;
  /** URL opcional de imagen que acompaña al enunciado de ESTA pregunta puntual */
  imagenUrl?: string;
  /** Instrucción de la sección a la que pertenece la pregunta (texto) */
  instruccionTexto?: string;
  /** Instrucción de la sección a la que pertenece la pregunta (imagen) */
  instruccionImagenUrl?: string;
  /** Opciones de respuesta */
  opciones: Opcion[];
  /** Número de pregunta actual (1-based) */
  numeroPregunta: number;
  /** Total de preguntas del formulario */
  totalPreguntas: number;
  /** Nombre del estudiante para personalizar la pantalla */
  nombreEstudiante?: string;
  /** Valor actualmente seleccionado (controlled) */
  valorSeleccionado?: number | null;
  /** Callback al seleccionar una opción (sin avanzar todavía) */
  onSeleccionar?: (valor: number) => void;
  /** Callback al confirmar la respuesta seleccionada */
  onConfirmar?: () => void;
  /** Callback al presionar "Anterior" */
  onAnterior?: () => void;
  /** true mientras se guarda la respuesta y se transiciona a la siguiente pregunta —
   *  bloquea las opciones para que un doble click no dispare dos avances */
  avanzando?: boolean;
  /** Si es la última pregunta, cambia el mensaje de "avanzando" */
  esUltima?: boolean;
  /** Fondo de acuerdo con la categoría del formulario. */
  background?: string;
}

// ── Componente ────────────────────────────────────────────────
export default function Reactivo({
  pregunta,
  imagenUrl,
  instruccionTexto,
  instruccionImagenUrl,
  opciones,
  numeroPregunta,
  totalPreguntas,
  nombreEstudiante,
  valorSeleccionado = null,
  onSeleccionar,
  onConfirmar,
  onAnterior,
  avanzando = false,
  esUltima = false,
  background = COLORS.neutro50,
}: ReactivoProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  // Animación de entrada: cada vez que cambia la pregunta (numeroPregunta),
  // el contenido arranca desplazado/transparente y "entra" en el siguiente
  // frame — un fundido + deslizamiento corto que deja claro que se avanzó
  // a una pregunta nueva, sin depender de ninguna librería de animación.
  const [entrando, setEntrando] = useState(false);
  useEffect(() => {
    setEntrando(false);
    const raf = requestAnimationFrame(() => setEntrando(true));
    return () => cancelAnimationFrame(raf);
  }, [numeroPregunta]);

  const progreso = ((numeroPregunta - 1) / totalPreguntas) * 100;
  const puedeAvanzar = valorSeleccionado !== null;
  const esPrimera = numeroPregunta === 1;

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
      background,
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}>

      {/* ── Barra superior ── */}
      <header style={{
        background: "#fff",
        borderBottom: `1px solid ${COLORS.neutro100}`,
        padding: "14px 28px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexShrink: 0,
      }}>
        {/* Logo PSYEVA */}
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: COLORS.violeta400,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <div style={{ width: 14, height: 14, borderRadius: 3, background: COLORS.verde400 }} />
        </div>

        {/* Barra de progreso */}
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            flex: 1, height: 6, borderRadius: 6,
            background: COLORS.neutro100, overflow: "hidden",
          }}>
            <div style={{
              height: "100%", borderRadius: 6,
              background: `linear-gradient(90deg, ${COLORS.violeta400}, ${COLORS.azul400})`,
              width: `${progreso}%`,
              transition: "width 0.4s ease",
            }} />
          </div>
          <span style={{ fontSize: 13, color: COLORS.neutro500, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
            {numeroPregunta} / {totalPreguntas}
          </span>
        </div>

        {/* Nombre del estudiante */}
        {nombreEstudiante && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "6px 12px", borderRadius: 20,
            background: COLORS.violeta50,
            border: `1px solid ${COLORS.violeta100}`,
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: "50%",
              background: COLORS.violeta400,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 600, color: "#fff",
            }}>
              {nombreEstudiante.charAt(0).toUpperCase()}
            </div>
            <span style={{ fontSize: 13, fontWeight: 500, color: COLORS.violeta600 }}>
              {nombreEstudiante}
            </span>
          </div>
        )}
      </header>

      {/* ── Contenido central ── */}
      <main style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
        maxWidth: 680,
        margin: "0 auto",
        width: "100%",
      }}>

        {/* Bloque que se anima al cambiar de pregunta (fundido + deslizamiento) */}
        <div style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          opacity: entrando ? 1 : 0,
          transform: entrando ? "translateY(0)" : "translateY(14px)",
          transition: "opacity 0.32s ease, transform 0.32s ease",
        }}>
          {/* Indicador de pregunta */}
          <p style={{
            fontSize: 12,
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: COLORS.violeta400,
            margin: "0 0 2px",
          }}>
            Pregunta {numeroPregunta}
          </p>

          {(instruccionTexto || instruccionImagenUrl) && (
            <div style={{
              width: "100%",
              marginBottom: 24,
              color: COLORS.neutro700,
            }}>
              {instruccionTexto && (
                <p style={{
                  fontSize: 16,
                  lineHeight: 1.6,
                  margin: "0 0 16px",
                  whiteSpace: "pre-wrap",
                }}>
                  {instruccionTexto}
                </p>
              )}
             
            </div>
          )}

          {/* Texto de la pregunta */}
          <h2 style={{
            fontSize: 22,
            fontWeight: 500,
            color: COLORS.neutro900,
            lineHeight: 1.45,
            margin: "0 0 24px",
          }}>
            {pregunta}
          </h2>

          {/* Imagen opcional del enunciado */}
          {imagenUrl && (
            <div style={{
              width: "100%",
              borderRadius: 12,
              marginBottom: 28,
              border: `1px solid ${COLORS.neutro100}`,
              background: "#fff",
              display: "flex",
              justifyContent: "center",
            }}>
              <img
                src={imagenUrl}
                alt="Imagen de apoyo para la pregunta"
                style={{ maxWidth: "100%", maxHeight: 420, width: "auto", height: "auto", objectFit: "contain", display: "block" }}
              />
            </div>
          )}

          {/* Opciones de respuesta */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            width: "100%",
            marginBottom: 24,
          }}>
            {opciones.map(opcion => {
              const seleccionada = valorSeleccionado === opcion.value;
              const enHover = hovered === opcion.value;
              const atenuada = avanzando && !seleccionada;

              return (
                <button
                  key={opcion.value}
                  onClick={() => { if (!avanzando) onSeleccionar?.(opcion.value); }}
                  onMouseEnter={() => setHovered(opcion.value)}
                  onMouseLeave={() => setHovered(null)}
                  disabled={avanzando}
                  style={{
                    width: "100%",
                    padding: "16px 20px",
                    borderRadius: 12,
                    border: `2px solid ${
                      seleccionada
                        ? COLORS.violeta400
                        : enHover
                        ? COLORS.violeta200
                        : COLORS.neutro100
                    }`,
                    background: seleccionada
                      ? COLORS.violeta50
                      : enHover
                      ? "#fafafe"
                      : "#fff",
                    cursor: avanzando ? "default" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    opacity: atenuada ? 0.45 : 1,
                    transition: "border-color 0.15s, background 0.15s, opacity 0.25s, transform 0.15s",
                    transform: seleccionada && avanzando ? "scale(1.015)" : "scale(1)",
                    textAlign: "left",
                  }}
                >
                  {/* Indicador circular */}
                  <div style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    flexShrink: 0,
                    border: `2px solid ${seleccionada ? COLORS.violeta400 : COLORS.neutro400}`,
                    background: seleccionada ? COLORS.violeta400 : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.15s",
                  }}>
                    {seleccionada && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>

                  {/* Etiqueta */}
                  <span style={{
                    fontSize: 16,
                    fontWeight: seleccionada ? 500 : 400,
                    color: seleccionada ? COLORS.violeta600 : COLORS.neutro900,
                    transition: "color 0.15s, font-weight 0.15s",
                  }}>
                    {opcion.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navegación — requiere seleccionar y luego confirmar */}
        <div style={{
          display: "flex",
          gap: 12,
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}>
          <button
            onClick={onAnterior}
            disabled={esPrimera || avanzando}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "13px 20px",
              borderRadius: 10,
              border: `1.5px solid ${COLORS.neutro100}`,
              background: "#fff",
              color: COLORS.neutro700,
              fontSize: 15,
              fontWeight: 500,
              cursor: (esPrimera || avanzando) ? "not-allowed" : "pointer",
              opacity: (esPrimera || avanzando) ? 0.5 : 1,
              transition: "opacity 0.15s",
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Anterior
          </button>

          <button
            onClick={onConfirmar}
            disabled={!puedeAvanzar || avanzando}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "13px 20px",
              borderRadius: 10,
              border: "none",
              background: !puedeAvanzar || avanzando ? COLORS.neutro200 : COLORS.violeta400,
              color: !puedeAvanzar || avanzando ? COLORS.neutro500 : "#fff",
              fontSize: 15,
              fontWeight: 600,
              cursor: (!puedeAvanzar || avanzando) ? "not-allowed" : "pointer",
              opacity: avanzando ? 0.75 : 1,
              transition: "opacity 0.15s, background 0.15s",
              flexShrink: 0,
            }}
          >
            {avanzando ? (esUltima ? "Finalizando..." : "Guardando...") : "Confirmar respuesta"}
          </button>

          {avanzando && (
            <span style={{
              fontSize: 13,
              fontWeight: 500,
              color: COLORS.violeta600,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: COLORS.violeta400,
                animation: "psyeva-pulso 0.9s ease-in-out infinite",
              }} />
              {esUltima ? "Finalizando..." : "Pasando a la siguiente..."}
            </span>
          )}
        </div>

        {/* Hint cuando no hay respuesta seleccionada */}
        {!puedeAvanzar && !avanzando && (
          <p style={{
            marginTop: 12,
            fontSize: 12,
            color: COLORS.neutro400,
            textAlign: "center",
          }}>
            Selecciona una opción y luego confirma para continuar
          </p>
        )}
      </main>

      {/* Keyframe del puntito animado de "avanzando" — inline porque el
          resto del proyecto no usa hojas de estilo, solo estilos inline. */}
      <style>{`
        @keyframes psyeva-pulso {
          0%, 100% { opacity: 0.3; transform: scale(0.85); }
          50%      { opacity: 1;   transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
